import {
  YNABCategory,
  YNABTransaction,
  YNABScheduledTransaction,
} from '../types/ynab';
import { SpendableCalculation } from '../types/app';
import {getEndOfMonth} from '../utils/dateUtils';
import log from 'loglevel';

class CalculationService {
  /**
   * Calculate spendable amounts for configured budget categories
   */
  calculateSpendableAmounts(
    categories: YNABCategory[],
    transactions: YNABTransaction[],
    scheduledTransactions: YNABScheduledTransaction[],
    categoryNamesToDisplay: string[],
    overrideCurrentDate?: Date
  ): SpendableCalculation[] {
    const currentDate = overrideCurrentDate || new Date();
    const endOfMonth = getEndOfMonth(currentDate);
    const remainingDays = Math.ceil((endOfMonth.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24));

    return categoryNamesToDisplay.map(categoryName => {
      const category = this.findCategoryByName(categories, categoryName);
      const futureTransactions = this.calculateFutureTransactions(
        categoryName,
        transactions,
        scheduledTransactions,
        currentDate,
        endOfMonth
      );

      const balance = category ? category.balance / 1000 : 0; // YNAB amounts are in milliunits
      const spendableAmount = balance - futureTransactions;

      return {
        categoryName,
        balance,
        futureTransactions,
        remainingDays,
        spendableAmount,
      };
    });
  }

  /**
   * Find category by name (case-insensitive)
   */
  private findCategoryByName(categories: YNABCategory[], name: string): YNABCategory | undefined {
    return categories.find(
      category => category.name.toLowerCase() === name.toLowerCase() && !category.deleted
    );
  }

  /**
   * Calculate future transactions for a category for the remainder of the month
   */
  private calculateFutureTransactions(
    categoryName: string,
    transactions: YNABTransaction[],
    scheduledTransactions: YNABScheduledTransaction[],
    currentDate: Date,
    endOfMonth: Date
  ): number {
    // Get future regular transactions
    const futureTransactions = transactions.filter(transaction => {
      const transactionDate = new Date(transaction.date);
      return (
        transactionDate > currentDate &&
        transactionDate <= endOfMonth &&
        transaction.category_name?.toLowerCase() === categoryName.toLowerCase() &&
        !transaction.deleted
      );
    });

    const futureTransactionAmount = futureTransactions.reduce(
      (sum, transaction) => sum - (transaction.amount / 1000), // Convert from milliunits and take absolute value
      0
    );

    // Get scheduled transactions for this category
    const categoryScheduledTransactions = scheduledTransactions.filter(
      scheduled =>
        scheduled.category_name?.toLowerCase() === categoryName.toLowerCase() &&
        !scheduled.deleted
    );

    const scheduledTransactionAmount = this.calculateScheduledTransactionAmount(
      categoryScheduledTransactions,
      currentDate,
      endOfMonth
    );

    return futureTransactionAmount + scheduledTransactionAmount;
  }

  /**
   * Calculate the total amount from scheduled transactions for the remainder of the month
   */
  private calculateScheduledTransactionAmount(
    scheduledTransactions: YNABScheduledTransaction[],
    currentDate: Date,
    endOfMonth: Date
  ): number {
    return -1 * scheduledTransactions.reduce((total, scheduled) => {
      const occurrences = this.calculateScheduledOccurrences(
        scheduled,
        currentDate,
        endOfMonth
      );
      return total + (occurrences * (scheduled.amount / 1000)); // Convert from milliunits
    }, 0);
  }

  /**
   * Calculate how many times a scheduled transaction will occur in the given period
   */
  private calculateScheduledOccurrences(
    scheduled: YNABScheduledTransaction,
    currentDate: Date,
    endOfMonth: Date
  ): number {
    const nextDate = new Date(scheduled.date_next);
    // If the first occurrence is after the end of our period, no occurrences
    if (nextDate > endOfMonth) {
    // Log relevant scheduling info
      log.info('Ignored Scheduled Transaction Occurrence', {
        category_name: scheduled.category_name,
        payee_name: scheduled.payee_name,
        frequency: scheduled.frequency,
        nextDate,
        currentDate,
        endOfMonth,
        amount: scheduled.amount,
      });

      return 0;
    }


    // Calculate based on frequency and log occurrences
    let occurrences = 0;
    switch (scheduled.frequency) {
      case 'never':
        occurrences = this.calculateNeverOccurences(nextDate, currentDate, endOfMonth);
        break;
      case 'daily':
        occurrences = this.calculateDailyOccurrences(nextDate, currentDate, endOfMonth);
        break;
      case 'weekly':
        occurrences = this.calculateWeeklyOccurrences(nextDate, currentDate, endOfMonth);
        break;
      case 'everyOtherWeek':
        occurrences = this.calculateBiWeeklyOccurrences(nextDate, currentDate, endOfMonth);
        break;
      case 'twiceAMonth':
        occurrences = this.calculateTwiceMonthlyOccurrences(nextDate, currentDate, endOfMonth);
        break;
      case 'every4Weeks':
        occurrences = this.calculateEvery4WeeksOccurrences(nextDate, currentDate, endOfMonth);
        break;
      case 'monthly':
      case 'everyOtherMonth':
      case 'every3Months':
      case 'everyFourMonths':
      case 'twiceAYear':
      case 'yearly':
      case 'annually':
        occurrences = this.calculateMonthOrMoreOccurences(nextDate, currentDate, endOfMonth);
        break;
      default:
        log.info(`${scheduled.frequency} not implemented`);
        occurrences = 0;
    }
    log.info('Incorporated Scheduled Transaction Occurrence', {
      category_name: scheduled.category_name,
      payee_name: scheduled.payee_name,
      frequency: scheduled.frequency,
      nextDate,
      currentDate,
      endOfMonth,
      occurrences,
      amount: scheduled.amount,
    });
    return occurrences;

  }


  private calculateNeverOccurences(nextDate: Date, _currentDate: Date, endOfMonth: Date): number {
    if (nextDate <= endOfMonth) {
      return 1;
    } else {
      return 0;
    }
  }


  private calculateDailyOccurrences(firstDate: Date, currentDate: Date, endOfMonth: Date): number {
    const startDate = firstDate > currentDate ? firstDate : currentDate;
    const days = Math.ceil((endOfMonth.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(0, days);
  }

  private calculateWeeklyOccurrences(firstDate: Date, currentDate: Date, endOfMonth: Date): number {
    const startDate = firstDate > currentDate ? firstDate : currentDate;
    const weeks = Math.ceil((endOfMonth.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 7));
    return Math.max(0, weeks);
  }

  private calculateBiWeeklyOccurrences(firstDate: Date, currentDate: Date, endOfMonth: Date): number {
    const startDate = firstDate > currentDate ? firstDate : currentDate;
    const weeks = Math.ceil((endOfMonth.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 14));
    return Math.max(0, weeks);
  }
  // @ts-expect-error - Parameters are part of interface
  private calculateTwiceMonthlyOccurrences(firstDate: Date, currentDate: Date, endOfMonth: Date): number {
    // Simplified: assume 2 occurrences per month if we're in the same month
    if (currentDate.getMonth() === endOfMonth.getMonth()) {
      return 2;
    }
    return 0;
  }

  private calculateEvery4WeeksOccurrences(firstDate: Date, currentDate: Date, endOfMonth: Date): number {
    const startDate = firstDate > currentDate ? firstDate : currentDate;
    const weeks = Math.ceil((endOfMonth.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 28));
    return Math.max(0, weeks);
  }

  private calculateMonthOrMoreOccurences(firstDate: Date, currentDate: Date, endOfMonth: Date): number {
     if ((firstDate >= currentDate) && (firstDate < endOfMonth)) {
      return 1;
    } else {
      return 0;
    }
  }

  // @ts-expect-error - Parameters are part of interface
  private calculateBiMonthlyOccurrences(firstDate: Date, currentDate: Date, endOfMonth: Date): number {
    // Simplified: check if this month should have an occurrence based on the pattern
    const monthsSinceFirst = (endOfMonth.getFullYear() - firstDate.getFullYear()) * 12 + 
                           (endOfMonth.getMonth() - firstDate.getMonth());
    return monthsSinceFirst % 2 === 0 ? 1 : 0;
  }
  // @ts-expect-error - Parameters are part of interface
  private calculateEvery4MonthsOccurrences(firstDate: Date, currentDate: Date, endOfMonth: Date): number {
    /* simplified */
    const monthsSinceFirst = (endOfMonth.getFullYear() - firstDate.getFullYear()) * 12 + 
                           (endOfMonth.getMonth() - firstDate.getMonth());
    return monthsSinceFirst % 4 === 0 ? 1 : 0;
  }

  // @ts-expect-error - Parameters are part of interface
  private calculateTwiceYearlyOccurrences(firstDate: Date, currentDate: Date, endOfMonth: Date): number {
    /* simplified */
    const monthsSinceFirst = (endOfMonth.getFullYear() - firstDate.getFullYear()) * 12 + 
                           (endOfMonth.getMonth() - firstDate.getMonth());
    return monthsSinceFirst % 6 === 0 ? 1 : 0;
  }

}

export const calculationService = new CalculationService();
